import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { toEffectiveRole, type AppUser } from '@shared/types';
import { isPlatformOwnerEmail } from '@shared/platformOwners';
import { SELECTED_LOCATION_KEY_PREFIX } from '@/features/location/storageKeys';

interface AuthContextValue {
  session: Session | null;
  user: AppUser | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function hydrate(s: Session | null) {
      if (cancelled) return;
      setSession(s);

      if (!s) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('users')
        .select(
          'id, company_id, primary_location_id, role, email, phone, first_name, last_name, employee_id, preferred_language, shift_end_time, disabled',
        )
        .eq('id', s.user.id)
        .maybeSingle();

      if (cancelled) return;

      if (error || !data || data.disabled) {
        // Mirror the server's auth boundary (server/trpc/context.ts:
        // `!profile || profile.disabled` → null user). A disabled account
        // still holds a valid Supabase JWT, so without this the SPA renders
        // the full role shell while every backend call 401s — a confusing
        // broken-shell UX (Sprint N F2). Treat it as signed-out and clear
        // the stale local session so ProtectedRoute bounces to /login.
        setUser(null);
        if (s && (!data || data.disabled)) {
          await supabase.auth.signOut({ scope: 'local' });
        }
      } else {
        setUser({
          id: data.id,
          companyId: data.company_id,
          primaryLocationId: data.primary_location_id,
          // Mirrors the server: a view-only login gets the manager screens,
          // and the server refuses its writes.
          ...toEffectiveRole(data.role),
          email: data.email,
          phone: data.phone,
          firstName: data.first_name,
          lastName: data.last_name,
          employeeId: data.employee_id,
          preferredLanguage: data.preferred_language ?? 'en',
          shiftEndTime: data.shift_end_time ?? '17:00',
          disabled: data.disabled,
          isPlatformOwner: isPlatformOwnerEmail(data.email),
        });
      }
      setIsLoading(false);
    }

    void (async () => {
      const { data } = await supabase.auth.getSession();
      await hydrate(data.session);
    })();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      void hydrate(newSession);
    });

    return () => {
      cancelled = true;
      authListener.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      isLoading,
      signOut: async () => {
        // Local scope only — global would revoke every active session for
        // this user across every device and browser. Multiple internal QA
        // sessions share the same real-user accounts (Shane, Mike, Dave) on
        // the pilot, and a global sign-out from one tab would 401 every
        // other tab mid-task. (Sprint I — caught real-user verification.)
        await supabase.auth.signOut({ scope: 'local' });
        // Cross-tenant hygiene (2026-06-23): purge every persisted shop-scope
        // selection so a prior tenant's location choice can't linger in this
        // browser across an account switch. Keys are userId-namespaced (so a
        // different user never reads a stale value), but on shared / QA devices
        // the orphan keys otherwise accumulate forever. Mirrors the TanStack
        // cross-tenant cache reset LocationScope already does.
        try {
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const k = localStorage.key(i);
            if (k && k.startsWith(SELECTED_LOCATION_KEY_PREFIX)) localStorage.removeItem(k);
          }
          // Shared-tablet hygiene (elite-audit P3, 2026-08-26): the resume-
          // banner pointer is single-slot and was surviving sign-out. Reads
          // are owner-filtered now (lastActiveThread.ts), so this is belt and
          // suspenders — a signed-out tablet should hold no thread pointer.
          localStorage.removeItem('fleetwise.lastActiveThread');
        } catch {
          // localStorage may be unavailable (private mode / SSR) — non-fatal.
        }
      },
      refreshProfile: async () => {
        // Re-triggers the hydrate path via an onAuthStateChange event (TOKEN_REFRESHED).
        await supabase.auth.refreshSession();
      },
    }),
    [session, user, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider.');
  return ctx;
}
